'use strict';

/***  hkt_personalization_greeting_card.js  ***/

class HktGreetingCard {
  constructor(container) {
    this.container       = container;
    this.gcProductHandle = container.dataset.productHandle;
    this.gcVariantId     = null; // resolved lazily via /products/[handle].js

    this.checkbox        = container.querySelector('#hkt-gc-checkbox');
    this.fieldsWrapper   = container.querySelector('#hkt-gc-fields');
    this.selectOcc       = container.querySelector('#hkt-gc-occasion');
    this.inputOccCustom  = container.querySelector('#hkt-gc-occasion-custom');
    this.inputName       = container.querySelector('#hkt-gc-recipient');
    this.textareaMsg     = container.querySelector('#hkt-gc-message');
    this.charCount       = container.querySelector('.hkt-gc__char-count');
    this.addBtn          = container.querySelector('#hkt-gc-add-btn');

    this._bindEvents();
  }

  // ─── Event wiring ────────────────────────────────────────────────────────

  _bindEvents() {
    this.checkbox.addEventListener('change', () => this._toggleFields());

    // Occasion select — show/hide custom input
    if (this.selectOcc && this.inputOccCustom) {
      this.selectOcc.addEventListener('change', () => this._toggleCustomOccasion());
    }

    if (this.textareaMsg && this.charCount) {
      this.textareaMsg.addEventListener('input', () => {
        this.charCount.textContent = `${this.textareaMsg.value.length} / 500`;
      });
    }

    [this.selectOcc, this.inputOccCustom, this.inputName, this.textareaMsg].forEach(field => {
      if (!field) return;
      field.addEventListener('change', () => this._clearError(field));
      field.addEventListener('input',  () => this._clearError(field));
    });

    if (this.addBtn) {
      this.addBtn.addEventListener('click', () => this._onAddBtnClick());
    }
  }

  // ─── UI helpers ──────────────────────────────────────────────────────────

  _toggleFields() {
    const checked = this.checkbox.checked;
    this.fieldsWrapper.hidden = !checked;
    this.fieldsWrapper.setAttribute('aria-hidden', String(!checked));
    this.checkbox.setAttribute('aria-expanded', String(checked));
    if (!checked) this._clearAllErrors();
  }

  _toggleCustomOccasion() {
    const isCustom = this.selectOcc.value === '__custom__';
    this.inputOccCustom.hidden = !isCustom;
    if (isCustom) {
      this.inputOccCustom.focus();
    } else {
      this.inputOccCustom.value = '';
      this._clearError(this.inputOccCustom);
    }
  }

  // Returns the final occasion value — custom text or selected option
  _getOccasionValue() {
    if (this.selectOcc.value === '__custom__') {
      return this.inputOccCustom ? this.inputOccCustom.value.trim() : '';
    }
    return this.selectOcc.value;
  }

  _clearError(field) {
    field.classList.remove('hkt-gc--invalid');
    const wrapper = field.closest('.hkt-gc__field');
    if (wrapper) {
      const msg = wrapper.querySelector('.hkt-gc__error-msg');
      if (msg) msg.hidden = true;
    }
  }

  _clearAllErrors() {
    [this.selectOcc, this.inputOccCustom, this.inputName, this.textareaMsg].forEach(f => {
      if (f) this._clearError(f);
    });
  }

  _showError(field) {
    field.classList.add('hkt-gc--invalid');
    const wrapper = field.closest('.hkt-gc__field');
    if (wrapper) {
      const msg = wrapper.querySelector('.hkt-gc__error-msg');
      if (msg) msg.hidden = false;
    }
  }

  _setLoading(loading) {
    if (!this.addBtn) return;
    const textEl    = this.addBtn.querySelector('.hkt-gc__add-btn-text');
    const loadingEl = this.addBtn.querySelector('.hkt-gc__add-btn-loading');
    this.addBtn.disabled = loading;
    if (textEl)    textEl.hidden    = loading;
    if (loadingEl) loadingEl.hidden = !loading;
  }

  // ─── Validation ──────────────────────────────────────────────────────────

  _validate() {
    let valid = true;

    // Occasion: either a list value or a non-empty custom text
    const occasionValue = this._getOccasionValue();
    if (!occasionValue) {
      // Show error on the visible field
      const errorField = (this.selectOcc.value === '__custom__' && this.inputOccCustom)
        ? this.inputOccCustom
        : this.selectOcc;
      this._showError(errorField);
      valid = false;
    }

    if (!this.inputName.value.trim())    { this._showError(this.inputName);   valid = false; }
    if (!this.textareaMsg.value.trim())  { this._showError(this.textareaMsg); valid = false; }

    if (!valid) {
      this.fieldsWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    return valid;
  }

  // ─── Custom Add to Cart click ─────────────────────────────────────────────

  _onAddBtnClick() {
    if (!this._validate()) return;

    if (!this.gcProductHandle) {
      this._showCartError('Greeting Card product handle not configured.');
      return;
    }

    this._setLoading(true);
    this._showCartError('');

    this._resolveVariantId()
      .then(variantId => {
        const payload = {
          items: [{
            id:       variantId,
            quantity: 1,
            properties: {
              'Occasion':       this._getOccasionValue(),
              'Recipient Name': this.inputName.value.trim(),
              'Message':        this.textareaMsg.value.trim(),
            },
          }],
        };

        return fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        });
      })
      .then(r => r.json())
      .then(data => {
        if (data.status) {
          const msg = data.description || data.message || 'Could not add greeting card. Please try again.';
          this._showCartError(msg);
          this._setLoading(false);
          return;
        }
        this._triggerOriginalAddToCart();
      })
      .catch(err => {
        this._showCartError('Something went wrong. Please try again.');
        this._setLoading(false);
        console.error('[HktGreetingCard]', err);
      });
  }

  // Fetch the first variant ID from /products/[handle].js (cached after first call)
  _resolveVariantId() {
    if (this.gcVariantId) return Promise.resolve(this.gcVariantId);

    return fetch(`/products/${this.gcProductHandle}.js`)
      .then(r => {
        if (!r.ok) throw new Error(`Product "${this.gcProductHandle}" not found.`);
        return r.json();
      })
      .then(product => {
        if (!product.variants || !product.variants.length) {
          throw new Error('No variants found for greeting card product.');
        }
        this.gcVariantId = product.variants[0].id;
        return this.gcVariantId;
      });
  }

  // Find and click the original "Add to cart" button (Bundle App handles this)
  // Uses offsetParent check to skip buttons inside display:none wrappers (e.g. desktop
  // section group hidden on mobile via CSS).
  _triggerOriginalAddToCart() {
    this._setLoading(false);

    const isVisible = el => el.offsetParent !== null;

    // Find the visible product-form custom element
    const productFormEl = Array.from(document.querySelectorAll('product-form')).find(isVisible);
    if (productFormEl) {
      const form = productFormEl.querySelector('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        return;
      }
    }

    // Fallback: find any visible submit / add button
    const btn = (
      Array.from(document.querySelectorAll('product-form [type="submit"]')).find(isVisible) ||
      Array.from(document.querySelectorAll('.product-form__submit')).find(isVisible) ||
      Array.from(document.querySelectorAll('button[name="add"]')).find(isVisible)
    );

    if (btn) {
      btn.click();
    } else {
      window.location.href = '/cart';
    }
  }

  _showCartError(message) {
    let el = this.container.querySelector('.hkt-gc__cart-error');
    if (!el) {
      el = document.createElement('p');
      el.className = 'hkt-gc__cart-error';
      this.addBtn.insertAdjacentElement('afterend', el);
    }
    el.textContent = message;
    el.hidden = !message;
  }
}

// ─── Bootstrap ───────────────────────────────────────────────────────────────
// Both desktop and mobile section groups are always in the DOM (one hidden via CSS).
// We initialize ALL upsell boxes so the visible one works regardless of viewport.

document.addEventListener('DOMContentLoaded', () => {
  // Upsell mode — init every instance (desktop + mobile section group)
  document.querySelectorAll('.hkt-greeting-card-upsell').forEach(el => new HktGreetingCard(el));

  // Standalone mode — char counter + custom occasion toggle
  document.querySelectorAll('.hkt-greeting-card-standalone').forEach(standalone => {
    // Char counter
    const textarea = standalone.querySelector('textarea[name="properties[Message]"]');
    const counter  = standalone.querySelector('.hkt-gc__char-count');
    if (textarea && counter) {
      textarea.addEventListener('input', () => {
        counter.textContent = `${textarea.value.length} / 500`;
      });
    }

    // Custom occasion: swap name attributes so only one submits as properties[Occasion]
    const select      = standalone.querySelector('#hkt-gc-occasion-sa');
    const customInput = standalone.querySelector('#hkt-gc-occasion-sa-custom');
    if (select && customInput) {
      select.addEventListener('change', () => {
        const isCustom = select.value === '__custom__';
        if (isCustom) {
          select.removeAttribute('name');
          select.removeAttribute('required');
          customInput.hidden   = false;
          customInput.disabled = false;
          customInput.required = true;
          customInput.focus();
        } else {
          select.setAttribute('name', 'properties[Occasion]');
          select.setAttribute('required', '');
          customInput.hidden   = true;
          customInput.disabled = true;
          customInput.required = false;
          customInput.value    = '';
        }
      });
    }
  });
});
